import { CreatePanelRequest } from 'features/drilldown'
import startCase from 'lodash/startCase'

import { ChatAction } from './gongChatApi'

const CREATE_PANELS_ACTION = 'create_panels'

/**
 * Extract the flattened list of panel requests from a message's structured
 * `create_panels` actions.
 */
export const extractPanelRequests = (
  actions: ReadonlyArray<ChatAction> | undefined
): ReadonlyArray<CreatePanelRequest> => {
  if (!actions) return []
  return actions
    .filter((action) => action.type === CREATE_PANELS_ACTION)
    .flatMap((action) => (Array.isArray(action.payload) ? action.payload : []))
}

/**
 * Human-readable summary of the panels a turn created, e.g.
 * "Created panels:\n * Map\n * Filter". Empty string when no panels.
 */
export const summarizePanelRequests = (
  panels: ReadonlyArray<CreatePanelRequest>
): string => {
  if (panels.length === 0) return ''
  const list = panels
    .map((panel) => `\n * ${startCase(panel.panelType)}`)
    .join('')
  return `${panels.length > 1 ? 'Created panels:' : 'Created panel:'}${list}`
}

/**
 * Build the assistant bubble content for a turn: the model's prose plus, when
 * panels were created, an appended summary. Falls back to the summary alone if
 * the model returned no prose.
 */
export const buildAssistantContent = (
  content: string,
  panels: ReadonlyArray<CreatePanelRequest>
): string => {
  const summary = summarizePanelRequests(panels)
  const trimmed = content.trim()
  if (!summary) return trimmed
  if (!trimmed) return summary
  return `${trimmed}\n\n${summary}`
}

/**
 * Utility function to strip markdown formatting for clipboard copy
 * This is a simple implementation that could be replaced with a library like 'remove-markdown' if needed
 */
export const stripMarkdown = (markdown: string): string => {
  let text = markdown

  // Remove HTML tags
  let prev
  do {
    prev = text
    text = text.replace(/<[^>]*>/g, '')
  } while (text !== prev)

  // Remove headers (###, ##, #)
  text = text.replace(/^#{1,6}\s+/gm, '')

  // Remove bold/italic markers
  text = text.replace(/\*\*([^*]+)\*\*/g, '$1')
  text = text.replace(/\*([^*]+)\*/g, '$1')
  text = text.replace(/__([^_]+)__/g, '$1')
  text = text.replace(/_([^_]+)_/g, '$1')

  // Remove code blocks
  text = text.replace(/```[^`]*```/g, '')
  text = text.replace(/`([^`]+)`/g, '$1')

  // Remove links but keep the text
  text = text.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')

  // Remove list markers
  text = text.replace(/^\s*[-*+]\s+/gm, '')
  text = text.replace(/^\s*\d+\.\s+/gm, '')

  // Remove blockquotes
  text = text.replace(/^\s*>\s+/gm, '')

  // Remove horizontal rules
  text = text.replace(/^---+$/gm, '')

  // Remove extra whitespace and trim
  text = text.replace(/\n{3,}/g, '\n\n')
  text = text.trim()

  return text
}
